/**
 * Connexion par NIP (design 4.2, plan 1.7). Le NIP seul suffit : il n'y a
 * pas de nom a choisir.
 *
 * 1. Blocage progressif en cours (store/pinLockout.js) : refus, sans rien
 *    verifier et sans compter l'essai.
 * 2. En ligne : POST /caisse/employees/verify-pin. Reussite : le verificateur
 *    PBKDF2 est garde pour cet employe (store/employees.js saveOnlineLogin).
 *    401 NIP incorrect : un verificateur local identique est efface (le NIP a
 *    change dans l'ERP). 409 : NIP partage, refuse comme le serveur.
 *    403 poste suspendu ou revoque : statut garde, rien d'efface.
 * 3. Hors ligne (ou serveur injoignable, en panne, sature) : UNE derivation
 *    PBKDF2, comparee en temps constant a tous les verificateurs du poste.
 *    Aucun : refus (employe jamais connecte en ligne ici, ou NIP faux : le
 *    poste ne peut pas les distinguer). Deux ou plus : NIP partage, refus.
 *
 * Toute erreur de NIP compte dans le blocage, en ligne comme hors ligne. Le
 * NIP en clair ne quitte pas cette fonction, sauf vers le serveur (HTTPS).
 */
import { forgetVerifier, getOrCreatePinSalt, listEmployees, normalizeRights, saveOnlineLogin } from '../store/employees';
import { getLockout, recordPinFailure, resetPinFailures } from '../store/pinLockout';
import { saveTerminalStatus } from './enrollment';
import { derivePinVerifier, isPinFormatValid, matchVerifier } from './pinVerifier';

export const LOGIN_STATUS = Object.freeze({
    OK: 'ok',
    LOCKED: 'locked',
    FORMAT: 'format',
    INCORRECT: 'incorrect',
    UNKNOWN_OFFLINE: 'unknown_offline',
    AMBIGUOUS: 'ambiguous',
    NOT_MANAGER: 'not_manager',
    SUSPENDED: 'suspended',
    REVOKED: 'revoked',
    TERMINAL_UNKNOWN: 'terminal_unknown',
});

function publicEmployee(employee) {
    return {
        employee_id: employee.employee_id,
        display_name: employee.display_name,
        rights: normalizeRights(employee.rights),
    };
}

/** Erreur de l'API qui veut dire « verifier hors ligne » (pas de reponse utile du serveur). */
function shouldFallBackOffline(error) {
    if (!error || !error.kind) return true;
    if (error.kind === 'network' || error.kind === 'timeout' || error.kind === 'invalid') return true;
    return error.kind === 'http' && (error.status >= 500 || error.status === 429);
}

async function fail(db, status, nowMs) {
    const lock = await recordPinFailure(db, nowMs);
    return { status, lock };
}

async function succeed(db, employee, mode, requireManager) {
    await resetPinFailures(db);
    const result = publicEmployee(employee);
    if (requireManager && !result.rights.is_manager) {
        return { status: LOGIN_STATUS.NOT_MANAGER, employee: result, mode };
    }
    return { status: LOGIN_STATUS.OK, employee: result, mode };
}

/**
 * @param {object} options
 * @param {IDBDatabase} options.db
 * @param {object} options.api client caisseApi
 * @param {string} options.token jeton du poste
 * @param {string} options.pin
 * @param {boolean} [options.offline] vrai si le poste se sait hors ligne : pas d'appel reseau
 * @param {boolean} [options.requireManager] confirmation d'un responsable (is_manager)
 * @param {number} [options.nowMs]
 * @param {SubtleCrypto} [options.subtle]
 * @returns {Promise<{ status: string, employee?: object, mode?: 'online'|'offline', lock?: object }>}
 */
export async function verifyPinLogin({ db, api, token, pin, offline = false, requireManager = false, nowMs, subtle }) {
    const at = () => (nowMs === undefined ? Date.now() : nowMs);
    const lock = await getLockout(db, at());
    if (lock.locked) return { status: LOGIN_STATUS.LOCKED, lock };
    if (!isPinFormatValid(pin)) return { status: LOGIN_STATUS.FORMAT };

    const salt = await getOrCreatePinSalt(db);
    const verifying = derivePinVerifier(pin, salt, subtle);

    if (!offline && token) {
        let employee = null;
        let apiError = null;
        try {
            [employee] = await Promise.all([api.verifyPin(token, pin), verifying]);
        } catch (error) {
            apiError = error;
        }
        if (employee) {
            await saveOnlineLogin(db, { employee, verifier: await verifying, now: new Date(at()) });
            return succeed(db, employee, 'online', requireManager);
        }
        // Une erreur de derivation (WebCrypto) remonte telle quelle.
        await verifying;
        if (!shouldFallBackOffline(apiError)) {
            const { status, code } = apiError;
            if (status === 403 && code === 'POSTE_DESACTIVE') {
                await saveTerminalStatus(db, 'DISABLED');
                return { status: LOGIN_STATUS.SUSPENDED };
            }
            if (status === 403 && code === 'POSTE_REVOQUE') {
                await saveTerminalStatus(db, 'REVOKED');
                return { status: LOGIN_STATUS.REVOKED };
            }
            if (status === 401 && code === 'POSTE_NON_RECONNU') return { status: LOGIN_STATUS.TERMINAL_UNKNOWN };
            if (status === 409) return fail(db, LOGIN_STATUS.AMBIGUOUS, at());
            if (status === 401) await forgetVerifier(db, await verifying);
            return fail(db, LOGIN_STATUS.INCORRECT, at());
        }
    }

    const verifier = await verifying;
    const matches = matchVerifier(verifier, await listEmployees(db));
    if (matches.length > 1) return fail(db, LOGIN_STATUS.AMBIGUOUS, at());
    if (matches.length === 0) return fail(db, LOGIN_STATUS.UNKNOWN_OFFLINE, at());
    return succeed(db, matches[0], 'offline', requireManager);
}

/** Message a l'ecran pour un resultat (design 4.2 et 8) : quoi faire, pas ce qui s'est passe. */
export function loginMessage(result) {
    switch (result && result.status) {
        case LOGIN_STATUS.INCORRECT:
        case LOGIN_STATUS.FORMAT:
            return 'NIP incorrect';
        case LOGIN_STATUS.UNKNOWN_OFFLINE:
            return 'Ce NIP n\'a jamais été utilisé sur ce poste. Il faut se connecter une fois quand Internet fonctionne.';
        case LOGIN_STATUS.AMBIGUOUS:
            return 'Ce NIP est partagé par plusieurs employés. Un responsable doit le changer dans l\'ERP.';
        case LOGIN_STATUS.NOT_MANAGER:
            return 'Ce NIP n\'est pas celui d\'un responsable.';
        case LOGIN_STATUS.TERMINAL_UNKNOWN:
            return 'L\'ERP ne reconnaît plus ce poste. Appelez le responsable.';
        default:
            return null;
    }
}
